import { useDataFastGoals } from "@/hooks/useDataFastGoals";
import prCarinaDetoni from "@/assets/igrejas/pessoas/pr-carina-detoni-ieq-central.webp";
import hetinyPhoto from "@/assets/igrejas/pessoas/hetiny-ieq-central.webp";
import IeqLogo from "@/assets/igrejas/98x98/ieqcentralpiracicaba.webp";
import { useSectionViewGoal } from "@/hooks/useSectionViewGoal";
import { buttonClass, CONTAINER, scrollToSection } from "./ui-tokens";

// Volume real da IEQ Central, medido no uso da plataforma — os mesmos números
// da página /stats (ver IMPACT_STATS em landing/solo/stats/page.tsx); se um
// mudar lá, mude aqui.
//
// Estes três só esta igreja pode dizer, e nenhum aparece em outro lugar da
// home: repetir aqui os agregados de `trusted-by` lia como
// evidência escassa, não como evidência forte.
//
// O 3.000 da IEQ saiu de toda a home de propósito: ao lado do "+4.400" do
// hero, ele lia como "3.000 dos 4.400 numa só igreja" e encolhia a plataforma.
// Ele segue publicado onde o total não aparece — /stats e a apresentação.
//
// O de escalas é o que mais trabalha: a página promete "15 minutos" sem nunca
// dimensionar. 300 escalas por mês é o que dá porte ao número.
const metrics = [
  { value: "300+", label: "escalas publicadas por mês" },
  { value: "1.400+", label: "visitantes acompanhados" },
  { value: "1.300+", label: "inscrições em eventos" },
];

// Depoimento é fala publicada, palavra por palavra: só o que veio pelo
// /avaliar e foi aprovado para o site (decisão do Vinicius, 03/11/2026). Trecho
// pode ser recortado, mas em frases inteiras e sem trocar palavra. O título é
// do Luiz Felipe; os cartões são de outras pessoas, para não ecoar.
const quotes = [
  {
    content:
      "Com o Ecclesiato, conseguimos centralizar todo esse processo. Cada voluntário informa diretamente sua indisponibilidade e, depois de configurarmos as funções e demais informações, o próprio sistema nos auxilia na elaboração das escalas de forma muito mais organizada e praticamente automática.",
    author: "Carina Detoni",
    role: "Pastora e líder do ministério infantil",
    image: prCarinaDetoni as string,
  },
  {
    content:
      "Simplesmente o sistema que facilita toda a gestão. Faço parte do administrativo da igreja, desde que adquirimos o Ecclesiato toda a gestão mudou.",
    author: "Hetiny",
    role: "Secretaria e administrativo",
    image: hetinyPhoto as string,
  },
];

export default function CaseIeq() {
  const { trackNavClicked } = useDataFastGoals();
  const ref = useSectionViewGoal("case_ieq");

  /* Esta seção já usou `content-visibility: auto`. Foi removido de propósito:
     ela fica ACIMA de #precos, o destino de quase todo CTA da landing, e a
     altura estimada nunca bate com a real — quando a seção era resolvida no
     meio da rolagem suave o CTA parava ~200px fora do lugar. Não reintroduza
     aqui sem resolver isso. */
  return (
    <section
      ref={ref}
      id="case-ieq"
      aria-labelledby="case-titulo"
      className="bg-lp-band py-20 text-lp-band-foreground md:py-28"
    >
      <div className={`${CONTAINER} grid gap-14 lg:grid-cols-12 lg:gap-16`}>
        <div className="lp-reveal lg:col-span-7">
          <div className="flex items-center gap-3">
            <img
              src={IeqLogo}
              alt=""
              className="size-11 rounded-full object-cover"
              width={44}
              height={44}
              loading="lazy"
            />
            <div>
              <p className="text-sm font-semibold">IEQ Central de Piracicaba</p>
              {/* Data absoluta, não "1 ano": envelhece a favor e não pede
                  manutenção. Tempo de casa é a prova de retenção mais forte
                  que um case tem. */}
              {/* "onde o Ecclesiato nasceu": é a igreja do fundador, e o case
                  diz isso em vez de esconder (ver founder.tsx). */}
              <p className="text-sm text-lp-band-foreground/70">
                Cliente desde agosto de 2025 · a igreja onde o Ecclesiato
                nasceu
              </p>
            </div>
          </div>

          <h2
            id="case-titulo"
            className="mt-8 text-3xl leading-[1.12] font-semibold tracking-tight text-balance md:text-[2.625rem]"
          >
            “O Ecclesiato realmente transformou os processos do meu
            ministério!”
          </h2>
          <p className="mt-4 text-sm text-lp-band-foreground/70">
            Luiz Felipe Saldanha, líder do Out Door
          </p>

          <p className="mt-6 max-w-xl text-base leading-relaxed text-pretty text-lp-band-foreground/75 md:text-lg">
            Antes, cada voluntário avisava um por um os dias em que não podia
            servir, e cada equipe montava a própria escala. Hoje a liderança
            administra a igreja pelo sistema, das escalas à secretaria.
          </p>

          <a
            href="#precos"
            onClick={(e) => {
              e.preventDefault();
              trackNavClicked("case_ieq_cta");
              scrollToSection("precos");
            }}
            className={buttonClass("inverse", "lg", "mt-10")}
          >
            Quero o mesmo resultado na minha igreja
          </a>
          {/* O risco dito colado em cada botão, não só no hero e nos preços:
              é aqui, logo depois da prova, que a vontade de assinar aparece. */}
          <p className="mt-3 text-sm text-lp-band-foreground/65">
            7 dias grátis · cobrança só no 8º dia · migração por nossa conta
          </p>
        </div>

        <div className="lp-reveal flex flex-col gap-10 lg:col-span-5">
          <dl className="grid grid-cols-3 gap-4 lg:grid-cols-1 lg:gap-6">
            {metrics.map(({ value, label }) => (
              <div key={label} className="flex flex-col-reverse gap-1">
                <dt className="text-sm leading-snug text-lp-band-foreground/70">
                  {label}
                </dt>
                <dd className="text-3xl font-semibold tracking-tight tabular-nums md:text-4xl">
                  {value}
                </dd>
              </div>
            ))}
          </dl>

          <div className="flex flex-col gap-3">
            {quotes.map((q) => (
              <figure
                key={q.author}
                className="rounded-2xl bg-lp-band-foreground/6 p-5 ring-1 ring-lp-band-foreground/10"
              >
                <blockquote className="text-sm leading-relaxed text-lp-band-foreground/90">
                  “{q.content}”
                </blockquote>
                <figcaption className="mt-4 flex items-center gap-3">
                  <img
                    src={q.image}
                    alt=""
                    className="size-8 rounded-full object-cover"
                    width={32}
                    height={32}
                    loading="lazy"
                  />
                  <span className="text-sm">
                    <span className="font-semibold">{q.author}</span>
                    <span className="text-lp-band-foreground/60">
                      {" "}
                      · {q.role}
                    </span>
                  </span>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
